import { useRef, useState, useMemo } from "react";
import type { DemoAttendeeRow } from "./sql";
import { companyKey, companyDisplayName } from "./helpers";
import type { ViewMode } from "./helpers";
import { PRIMARY } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

// Per-stage lists under the butterfly. In attendee mode, one row per attendee
// across columns, click to pin across stages. In company mode, one row per
// prospect company with an (N) attendee count and expand chevron; expanding
// reveals the company's attendees that reached that column's stage. Pinning
// an attendee auto-expands their company in every column.

type Column = { label: string; predicate: (r: DemoAttendeeRow) => boolean };

function buildColumns(splitPaid: boolean): Column[] {
  const cols: Column[] = [
    { label: "Demo run", predicate: () => true },
    { label: "Signed up", predicate: (r) => r.signup_timing_bucket !== "never" },
    { label: "Activated", predicate: (r) => r.activated },
  ];
  if (splitPaid) {
    cols.push({ label: "Converted to Plus", predicate: (r) => r.on_paid_plan === true && r.billing_plan === "plus" });
    cols.push({ label: "Converted to Pro", predicate: (r) => r.on_paid_plan === true && r.billing_plan === "pro" });
  } else {
    cols.push({ label: "Converted to paid", predicate: (r) => r.on_paid_plan === true });
  }
  return cols;
}

function attendeeName(r: DemoAttendeeRow): string {
  return (r.user_full_name ?? "").trim();
}

function attendeeKey(r: DemoAttendeeRow): string {
  return r.attendee_email;
}

function attendeeTitle(r: DemoAttendeeRow): string {
  const name = attendeeName(r);
  const head = name ? `${name} (${r.attendee_email})` : r.attendee_email;
  const company = r.company_name_from_user ?? r.company_name_native;
  return company ? `${head}, ${company}` : head;
}

type CompanyGroup = {
  key: string;
  name: string;
  attendees: DemoAttendeeRow[];
};

function buildCompanyGroups(rows: DemoAttendeeRow[]): CompanyGroup[] {
  const map = new Map<string, { name: string; byEmail: Map<string, DemoAttendeeRow> }>();
  for (const r of rows) {
    const k = companyKey(r);
    let g = map.get(k);
    if (!g) {
      g = { name: companyDisplayName(r), byEmail: new Map() };
      map.set(k, g);
    }
    if (!g.byEmail.has(r.attendee_email)) g.byEmail.set(r.attendee_email, r);
  }
  return Array.from(map.entries()).map(([key, g]) => ({
    key,
    name: g.name,
    attendees: Array.from(g.byEmail.values()),
  }));
}

export function DemoFunnelColumnLists({
  rows,
  splitPaid = false,
  viewMode,
  highlightedCompanyKey = null,
  onHighlightCompany,
  highlightedAttendeeKey = null,
  onHighlightAttendee,
  pagePills,
  selectionLabel,
  onClearSelection,
}: {
  rows: DemoAttendeeRow[];
  splitPaid?: boolean;
  viewMode: ViewMode;
  highlightedCompanyKey?: string | null;
  onHighlightCompany?: (key: string | null) => void;
  highlightedAttendeeKey?: string | null;
  onHighlightAttendee?: (key: string | null) => void;
  pagePills?: FilterPillSpec[];
  selectionLabel?: string | null;
  onClearSelection?: () => void;
}) {
  const isCompany = viewMode === "company";
  const highlightedKey = highlightedAttendeeKey;
  const setHighlightedKey = (next: string | null) => onHighlightAttendee?.(next);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const sectionRef = useRef<HTMLElement | null>(null);

  const dedupedByAttendee = useMemo(() => {
    const seen = new Map<string, DemoAttendeeRow>();
    for (const r of rows) {
      const k = attendeeKey(r);
      if (!seen.has(k)) seen.set(k, r);
    }
    return Array.from(seen.values());
  }, [rows]);

  const companyGroups = useMemo(() => buildCompanyGroups(rows), [rows]);

  const columns = buildColumns(splitPaid);
  const q = search.trim().toLowerCase();

  const matchesAttendeeSearch = (r: DemoAttendeeRow): boolean => {
    if (!q) return true;
    const name = attendeeName(r).toLowerCase();
    return (
      name.includes(q) ||
      r.attendee_email.toLowerCase().includes(q) ||
      (r.company_name_from_user ?? "").toLowerCase().includes(q) ||
      (r.company_name_native ?? "").toLowerCase().includes(q)
    );
  };

  const matchesCompanySearch = (g: CompanyGroup): boolean => {
    if (!q) return true;
    if (g.name.toLowerCase().includes(q)) return true;
    return g.attendees.some(matchesAttendeeSearch);
  };

  // In company mode the search auto-expands any company whose match is on an
  // attendee (so the matching email is visible without manually clicking).
  const autoExpanded = useMemo<Set<string>>(() => {
    if (!isCompany || !q) return new Set();
    const out = new Set<string>();
    for (const g of companyGroups) {
      if (g.name.toLowerCase().includes(q)) continue;
      if (g.attendees.some(matchesAttendeeSearch)) out.add(g.key);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyGroups, q, isCompany]);

  // Pinning an attendee auto-expands their company in every column. Either
  // an in-list attendee-pin or a cross-panel company highlight can drive this.
  const pinnedCompanyKey = useMemo<string | null>(() => {
    if (!isCompany) return null;
    if (highlightedCompanyKey) return highlightedCompanyKey;
    if (!highlightedKey) return null;
    const row = dedupedByAttendee.find((r) => attendeeKey(r) === highlightedKey);
    return row ? companyKey(row) : null;
  }, [highlightedKey, highlightedCompanyKey, isCompany, dedupedByAttendee]);

  const isExpanded = (key: string): boolean =>
    expanded.has(key) || autoExpanded.has(key) || key === pinnedCompanyKey;

  const handleCompanyNameClick = (key: string) => {
    if (!onHighlightCompany) return;
    onHighlightCompany(highlightedCompanyKey === key ? null : key);
    if (sectionRef.current) {
      sectionRef.current.querySelectorAll<HTMLElement>(".funnel-col-list").forEach((el) => {
        el.scrollTop = 0;
      });
    }
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleAttendeeClick = (row: DemoAttendeeRow) => {
    const key = attendeeKey(row);
    const next = highlightedKey === key ? null : key;
    setHighlightedKey(next);
    // Pinning an attendee also pins their company so the highlight propagates
    // to the drilldown table and any company-keyed consumers.
    if (next) {
      onHighlightCompany?.(companyKey(row));
    } else {
      onHighlightCompany?.(null);
    }
    if (next && sectionRef.current) {
      sectionRef.current.querySelectorAll<HTMLElement>(".funnel-col-list").forEach((el) => {
        el.scrollTop = 0;
      });
    }
  };

  const filteredAttendees = q ? dedupedByAttendee.filter(matchesAttendeeSearch) : dedupedByAttendee;
  const filteredGroups = q ? companyGroups.filter(matchesCompanySearch) : companyGroups;

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  if (selectionLabel) {
    pills.unshift({
      kind: "selection",
      label: `Selected: ${selectionLabel}`,
      onClear: onClearSelection,
    });
  }

  return (
    <section className="card" ref={sectionRef}>
      <SectionCardHeader
        title={isCompany ? "Companies by funnel stage" : "Attendees by funnel stage"}
        subtitle={
          isCompany
            ? "One list per stage. Each row is a prospect company with attendee count; click the chevron to expand. Click an attendee to pin them (and their company) across every stage they reach."
            : "One list per stage. Click an attendee to highlight and pin them across every stage they reach."
        }
        filterPills={pills}
        headerExtra={
          <span className="input-wrap" style={{ minWidth: 220 }}>
            <i className="ti ti-search" />
            <input
              type="text"
              className="input"
              placeholder={isCompany ? "Search company, name, email" : "Search name, email, company"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </span>
        }
      />
      <div className="card-body">
        <div
          className="funnel-cols"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
            gap: 12,
          }}
        >
          {columns.map((col) => {
            if (isCompany) {
              const stageGroupsRaw = filteredGroups
                .map((g) => {
                  const passing = g.attendees.filter(col.predicate);
                  return passing.length > 0 ? { ...g, attendees: passing } : null;
                })
                .filter((g): g is CompanyGroup => g !== null)
                .sort((a, b) => b.attendees.length - a.attendees.length || a.name.localeCompare(b.name));
              // Pull pinned company to the top so the user can scan its row
              // across every column without scrolling.
              const stageGroups = pinnedCompanyKey
                ? [
                    ...stageGroupsRaw.filter((g) => g.key === pinnedCompanyKey),
                    ...stageGroupsRaw.filter((g) => g.key !== pinnedCompanyKey),
                  ]
                : stageGroupsRaw;
              const totalAttendees = stageGroups.reduce((s, g) => s + g.attendees.length, 0);
              const dimmed = pinnedCompanyKey != null &&
                !stageGroups.some((g) => g.key === pinnedCompanyKey);
              return (
                <div key={col.label} className="funnel-col">
                  <div className="funnel-col-h">{col.label}</div>
                  <div className="funnel-col-counts">
                    <span style={{ color: PRIMARY }}>
                      <span className="dot" style={{ background: PRIMARY }} /> {stageGroups.length} companies · {totalAttendees}
                    </span>
                  </div>
                  <ul className={`funnel-col-list${dimmed ? " is-dimmed" : ""}`}>
                    {stageGroups.length === 0 && <li className="funnel-col-empty">—</li>}
                    {stageGroups.map((g) => {
                      const open = isExpanded(g.key);
                      const isPinnedCompany = g.key === pinnedCompanyKey;
                      return (
                        <li
                          key={`co-${g.key}`}
                          className={isPinnedCompany ? "is-highlighted" : ""}
                          style={{ display: "block", padding: 0 }}
                        >
                          <div
                            title={g.name}
                            style={{
                              display: "flex", alignItems: "center", gap: 6,
                              padding: "4px 6px",
                            }}
                          >
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleExpanded(g.key); }}
                              aria-label={open ? "Collapse" : "Expand"}
                              style={{
                                background: "transparent", border: "none", padding: 0, margin: 0,
                                cursor: "pointer", color: "var(--fg-3)", lineHeight: 1,
                              }}
                            >
                              <i className={`ti ${open ? "ti-chevron-down" : "ti-chevron-right"}`} style={{ fontSize: 12 }} />
                            </button>
                            <span className="path-dot" style={{ background: PRIMARY }} />
                            <span
                              onClick={() => handleCompanyNameClick(g.key)}
                              className="funnel-col-text"
                              style={{ flex: 1, minWidth: 0, cursor: onHighlightCompany ? "pointer" : "default" }}
                            >
                              <span className="funnel-col-name" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {g.name}
                              </span>
                            </span>
                            <span style={{ fontSize: 11, color: "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>
                              ({g.attendees.length})
                            </span>
                          </div>
                          {open && (
                            <ul style={{
                              listStyle: "none", margin: 0, padding: 0,
                              borderLeft: "2px solid var(--border-default)",
                              marginLeft: 14,
                            }}>
                              {g.attendees.map((r) => {
                                const k = attendeeKey(r);
                                const name = attendeeName(r);
                                return (
                                  <li
                                    key={`a-${g.key}-${k}`}
                                    title={attendeeTitle(r)}
                                    className={highlightedKey === k ? "is-highlighted" : ""}
                                    onClick={(e) => { e.stopPropagation(); handleAttendeeClick(r); }}
                                    style={{ cursor: "pointer", padding: "3px 6px 3px 10px" }}
                                  >
                                    <span className="funnel-col-text">
                                      {name && <span className="funnel-col-name">{name}</span>}
                                      <span className="funnel-col-email">{r.attendee_email}</span>
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            }

            // Attendee mode (original behaviour)
            const all = filteredAttendees.filter(col.predicate);
            const pinned = highlightedKey
              ? all.find((r) => attendeeKey(r) === highlightedKey) ?? null
              : null;
            const others = pinned ? all.filter((r) => attendeeKey(r) !== highlightedKey) : all;
            const dimmed = !!highlightedKey && !pinned;
            return (
              <div key={col.label} className="funnel-col">
                <div className="funnel-col-h">{col.label}</div>
                <div className="funnel-col-counts">
                  <span style={{ color: PRIMARY }}>
                    <span className="dot" style={{ background: PRIMARY }} /> {all.length}
                  </span>
                </div>
                <ul className={`funnel-col-list${dimmed ? " is-dimmed" : ""}`}>
                  {all.length === 0 && <li className="funnel-col-empty">—</li>}
                  {pinned && (
                    <li
                      key={`p-${attendeeKey(pinned)}`}
                      title={attendeeTitle(pinned)}
                      className="is-highlighted"
                      onClick={() => handleAttendeeClick(pinned)}
                      style={{ cursor: "pointer" }}
                    >
                      <span className="path-dot" style={{ background: PRIMARY }} />
                      <span className="funnel-col-text">
                        {attendeeName(pinned) && <span className="funnel-col-name">{attendeeName(pinned)}</span>}
                        <span className="funnel-col-email">{pinned.attendee_email}</span>
                      </span>
                    </li>
                  )}
                  {others.map((r) => {
                    const key = attendeeKey(r);
                    const name = attendeeName(r);
                    return (
                      <li
                        key={`r-${key}`}
                        title={attendeeTitle(r)}
                        className={highlightedKey === key ? "is-highlighted" : ""}
                        onClick={() => handleAttendeeClick(r)}
                        style={{ cursor: "pointer" }}
                      >
                        <span className="path-dot" style={{ background: PRIMARY }} />
                        <span className="funnel-col-text">
                          {name && <span className="funnel-col-name">{name}</span>}
                          <span className="funnel-col-email">{r.attendee_email}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
